import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Toast,
  ToastType,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '../../components/ui'
import { VehicleSelectionModal } from '../../components/proposals'
import { LeadInterest } from '../../types'
import { useLeadEdit } from './presentation/hooks'
import {
  LeadEditHeader,
  LeadEditTabs,
  LeadEditLoading,
  LeadEditError,
} from './presentation/components'

export const LeadEditPage: React.FC = () => {
  const navigate = useNavigate()
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)

  const {
    lead,
    formData,
    formattedValues,
    isLoading,
    error,
    isSubmitting,
    checkPhoneWarning,
    isCheckingPhone,
    checkDocumentWarning,
    isCheckingDocument,
    showVehicleSelection,
    setShowVehicleSelection,
    handleSubmit,
    handleCancel,
    updateFormData,
    updateFormattedValues,
    validationErrors,
    id,
    isCreating,
    duplicateLeadId,
    showDuplicatePopup,
    setShowDuplicatePopup,
    onNextStepValidation,
    clearDuplicateData,
  } = useLeadEdit()

  const handleSubmitWithToast = async () => {
    const result = await handleSubmit()
    if (result) {
      setToast({
        message: result.message,
        type: result.success ? 'success' : 'error',
      })
    }
  }

  const handleCreateProposal = () => {
    if (!id) return

    if (formData.vehicleInterests && formData.vehicleInterests.length > 0) {
      if (formData.vehicleInterests.length > 1) {
        setShowVehicleSelection(true)
      } else {
        navigate(
          `/proposals/new?leadId=${id}&vehicleId=${formData.vehicleInterests[0].vehicleId}`
        )
      }
    } else {
      navigate('/proposals/new')
    }
  }

  const handleCreateProposalForVehicle = (vehicleId: string) => {
    if (!id) return
    navigate(`/proposals/new?leadId=${id}&vehicleId=${vehicleId}`)
  }

  const handleInterestChange = (field: keyof LeadInterest, value: any) => {
    updateFormData({
      interest: {
        ...formData.interest,
        [field]: value,
      },
    })
  }

  const handleMultiSelectChange = (field: keyof LeadInterest, value: string) => {
    const currentValues = (formData.interest[field] as string[]) || []
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value]

    updateFormData({
      interest: {
        ...formData.interest,
        [field]: newValues,
      },
    })
  }

  const handleNextStep = (currentStep: number, targetStep: number): boolean => {
    return onNextStepValidation(currentStep, targetStep)
  }

  const handleDuplicateExit = () => {
    clearDuplicateData()
  }

  const handleDuplicateRedirect = () => {
    setShowDuplicatePopup(false)
    if (duplicateLeadId) {
      navigate(`/leads/edit/${duplicateLeadId}`)
    }
  }

  if (isLoading) {
    return <LeadEditLoading />
  }

  if (error && !isCreating) {
    return <LeadEditError error={error} />
  }

  if (!isCreating && !lead) {
    return <LeadEditError error="Lead não encontrado" />
  }

  // Only pass detailed vehicle interests (from the saved lead) to the modal.
  // formData.vehicleInterests is the simplified VehicleInterest[] type and
  // does NOT have the nested .vehicle object, which would crash VehicleSelectionModal.
  const detailedVehiclesForModal = lead?.vehicleInterest || []

  return (
    <div className="max-w-5xl mx-auto w-full space-y-6">
      {/* Header */}
      <LeadEditHeader
        lead={lead}
        formData={formData}
        isSubmitting={isSubmitting}
        isCreating={isCreating}
        onCancel={handleCancel}
        onSubmit={handleSubmitWithToast}
        onCreateProposal={handleCreateProposal}
      />

      {/* Form */}
      <Card className="border-border shadow-sm">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-lg font-semibold tracking-tight">Dados do Lead</CardTitle>
          <p className="text-sm text-muted-foreground">
            Preencha as etapas abaixo. Layout em coluna única para leitura mais confortável.
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <LeadEditTabs
            lead={lead}
            formData={formData}
            formattedValues={formattedValues}
            checkPhoneWarning={checkPhoneWarning}
            checkDocumentWarning={checkDocumentWarning}
            onFormDataChange={updateFormData}
            onFormattedValuesChange={updateFormattedValues}
            onInterestChange={handleInterestChange}
            onMultiSelectChange={handleMultiSelectChange}
            onCreateProposal={handleCreateProposalForVehicle}
            leadId={id || undefined}
            onCancel={handleCancel}
            onSubmit={handleSubmitWithToast}
            isSubmitting={isSubmitting}
            validationErrors={validationErrors}
            onNextStep={handleNextStep}
            duplicateLeadId={duplicateLeadId}
            onDuplicateExit={handleDuplicateExit}
            onDuplicateRedirect={handleDuplicateRedirect}
            showDuplicatePopup={showDuplicatePopup}
            isCheckingPhone={isCheckingPhone}
            isCheckingDocument={isCheckingDocument}
          />
        </CardContent>
      </Card>

      {showVehicleSelection && (
        <VehicleSelectionModal
          isOpen={showVehicleSelection}
          onClose={() => setShowVehicleSelection(false)}
          onSelect={(vehicleId) => {
            navigate(`/proposals/new?leadId=${id}&vehicleId=${vehicleId}`)
          }}
          vehicles={detailedVehiclesForModal}
          leadName={formData.name || lead?.name || ''}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
    </div>
  )
}
